import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * @param {string} text
 * @returns {{ value: number, suffix: string, decimals: number } | null}
 */
function parseStatValue(text) {
	const raw = text.trim();
	const match = raw.match(/^([\d.,]+)(.*)$/);

	if (!match) {
		return null;
	}

	const numberPart = match[1].replace(/,/g, '');
	const value = Number.parseFloat(numberPart);

	if (Number.isNaN(value)) {
		return null;
	}

	const decimalSegment = numberPart.includes('.') ? numberPart.split('.')[1] : '';

	return {
		value,
		suffix: match[2] ?? '',
		decimals: decimalSegment.length,
	};
}

/**
 * @param {HTMLElement} el
 * @param {{ value: number, suffix: string, decimals: number }} parsed
 * @param {number} current
 */
function formatStatValue(el, parsed, current) {
	const { suffix, decimals } = parsed;
	const display =
		decimals > 0 ? current.toFixed(decimals) : String(Math.round(current));
	el.textContent = `${display}${suffix}`;
}

/**
 * @param {HTMLElement} panel
 * @param {{ force?: boolean }} [options]
 */
export function animateBeforeAfterPanelStats(panel, options = {}) {
	const { force = false } = options;

	if (panel.dataset.statsAnimated === 'true' && !force) {
		return;
	}

	const statValues = panel.querySelectorAll('.js-before-after-stat-value');
	const targets = [];

	statValues.forEach((el) => {
		const finalText = (el.dataset.statFinal ?? el.textContent).trim();
		const parsed = parseStatValue(finalText);

		if (!parsed) {
			return;
		}

		el.dataset.statFinal = finalText;
		targets.push({ el, parsed });
	});

	if (!targets.length) {
		return;
	}

	const timeline = gsap.timeline({
		defaults: { ease: 'power2.out' },
		onComplete: () => {
			panel.dataset.statsAnimated = 'true';
		},
	});

	targets.forEach(({ el, parsed }, index) => {
		const counter = { value: 0 };

		el.textContent = `0${parsed.suffix}`;

		timeline
			.from(
				el,
				{
					y: 14,
					autoAlpha: 0,
					duration: 0.55,
					ease: 'power3.out',
				},
				index * 0.14,
			)
			.to(
				counter,
				{
					value: parsed.value,
					duration: 1.35,
					ease: 'power2.out',
					onUpdate: () => formatStatValue(el, parsed, counter.value),
				},
				index * 0.14,
			);
	});
}

/**
 * @param {HTMLElement} section
 */
function bindSectionScroll(section) {
	const firstPanel = section.querySelector('.before-after__category-panel.is-active')
		?? section.querySelector('.before-after__category-panel');

	if (!firstPanel) {
		return null;
	}

	const triggerEl = section.querySelector('.before-after__stats') ?? section;

	const trigger = ScrollTrigger.create({
		trigger: triggerEl,
		start: 'top 82%',
		once: true,
		onEnter: () => animateBeforeAfterPanelStats(firstPanel),
	});

	return () => trigger.kill();
}

export function initBeforeAfterStatsAnimation() {
	const sections = document.querySelectorAll('.js-before-after-section');

	if (!sections.length) {
		return;
	}

	const cleanups = [];

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
		sections.forEach((section) => {
			const cleanup = bindSectionScroll(section);

			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => {
			cleanups.forEach((fn) => fn());
		};
	});
}
